import React, { useState, useMemo } from "react";
import Carousel from "react-multi-carousel";
import "react-multi-carousel/lib/styles.css";
import "./MediaCarousel.css";
import Lightbox from "yet-another-react-lightbox";
import Video from "yet-another-react-lightbox/plugins/video";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Captions from "yet-another-react-lightbox/plugins/captions";
import Slideshow from "yet-another-react-lightbox/plugins/slideshow";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

const MediaCarousel = ({ images, title, animation }) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const shouldAnimate = animation && animation.animationType && animation.animationType !== 'none';

  // Utility checkers
  const isVideoFile = (filePath) =>
    /\.(mp4|mov|webm|avi|mkv|m4v|ogg|wmv)$/i.test(filePath);

  const isImageFile = (filePath) =>
    /\.(jpg|jpeg|png|gif|bmp|webp|svg|heic|heif|tiff|tif)$/i.test(filePath);

  // Memoized media processing for better performance
  const processed = useMemo(() => {
    if (!images || !Array.isArray(images) || images.length === 0) {
      return { mediaItems: [], lightboxSlides: [] };
    }

    // Step 1: classify media first
    const classifiedItems = images
      .map((url) => {
        if (!url || typeof url !== "string") return null;
        if (isVideoFile(url)) return { type: "video", url };
        if (isImageFile(url)) return { type: "image", url };
        return { type: "embed", url };
      })
      .filter(Boolean);

    // Step 2: then build slides for lightbox
    const lightboxSlides = classifiedItems
      .filter((item) => item.type !== "embed")
      .map((item) => {
        if (item.type === "video") {
          const ext = item.url.split(".").pop().toLowerCase();
          let mime = "video/mp4";
          if (ext === "webm") mime = "video/webm";
          else if (ext === "ogg") mime = "video/ogg";
          else if (ext === "mov") mime = "video/quicktime";
          else if (ext === "avi") mime = "video/x-msvideo";
          else if (ext === "mkv") mime = "video/x-matroska";
          else if (ext === "m4v") mime = "video/x-m4v";
          else if (ext === "wmv") mime = "video/x-ms-wmv";

          return {
            type: "video",
            sources: [{ src: item.url, type: mime }],
            title: "Video",
          };
        }
        return { src: item.url, title: "Image" };
      });

    return { mediaItems: classifiedItems, lightboxSlides };
  }, [images]);

  const { mediaItems, lightboxSlides } = processed;

  // Responsive config for react-multi-carousel
  const responsive = useMemo(
    () => ({
      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 3,
        slidesToSlide: 3,
      },
      tablet: {
        breakpoint: { max: 1024, min: 576 },
        items: 2,
        slidesToSlide: 2,
      },
      mobile: {
        breakpoint: { max: 576, min: 0 },
        items: 1,
        slidesToSlide: 1,
      },
    }),
    []
  );

  // Handle click to open lightbox
  const handleMediaClick = (item, itemIdx) => {
    const slideIndex = lightboxSlides.findIndex(
      (s) =>
        (item.type === "video" &&
          s.type === "video" &&
          s.sources[0].src === item.url) ||
        (item.type === "image" && s.src === item.url)
    );

    if (slideIndex >= 0) {
      setSelectedIndex(slideIndex);
      setIsViewerOpen(true);
    }
  };

  // Show empty state if no media
  if (mediaItems.length === 0) {
    return (
      <div className="container">
        <div className="bootstrap-carousel-wrapper">
          <h2 className="text-center mb-4">{title || "Media Carousel"}</h2>
          <div className="text-center py-5">
            <p className="text-muted">No media items to display</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container" {...(shouldAnimate ? {
      'data-aos': animation.animationType,
      'data-aos-duration': Number(animation.animationDuration),
      'data-aos-delay': Number(animation.animationDelay),
    } : {})}>
      <div className="bootstrap-carousel-wrapper">
        {isViewerOpen && (
          <Lightbox
            open={isViewerOpen}
            close={() => setIsViewerOpen(false)}
            index={selectedIndex}
            slides={lightboxSlides}
            plugins={[Video, Fullscreen, Captions, Slideshow, Zoom]}
          />
        )}

        <h2 className="text-center mb-4">{title || "Media Carousel"}</h2>

        <Carousel
          responsive={responsive}
          infinite={false}
          draggable
          swipeable
          showDots={mediaItems.length > 1}
          arrows
          containerClass="custom-carousel"
          itemClass="custom-carousel-item"
          renderButtonGroupOutside={false}
          keyBoardControl
        >
          {mediaItems.map((item, itemIdx) => (
            <div key={`${item.type}-${itemIdx}`} className="carousel-slide-content">
              <div className="carousel-card">
                {item.type === "embed" ? (
                  <iframe
                    src={item.url}
                    className="carousel-media"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={`Embedded content ${itemIdx + 1}`}
                  />
                ) : item.type === "video" ? (
                  <div className="carousel-media-container">
                    <video
                      src={item.url}
                      className="carousel-media"
                      onClick={() => handleMediaClick(item, itemIdx)}
                      muted
                      preload="metadata"
                    />
                    <div
                      className="play-overlay"
                      onClick={() => handleMediaClick(item, itemIdx)}
                    >
                      <i className="fas fa-play"></i>
                    </div>
                  </div>
                ) : (
                  <img
                    src={item.url}
                    className="carousel-media"
                    alt={`Media item ${itemIdx + 1}`}
                    onClick={() => handleMediaClick(item, itemIdx)}
                    loading="lazy"
                    onError={(e) => {
                      e.target.style.display = "none";
                      const errorElement = e.target.nextSibling;
                      if (errorElement) {
                        errorElement.style.display = "flex";
                      }
                    }}
                  />
                )}
                <div className="media-error" style={{ display: "none" }}>
                  <i className="fas fa-exclamation-triangle"></i>
                  <span>Failed to load media</span>
                </div>
              </div>
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
};

export default MediaCarousel;
